import { config } from "@nova/configuration/browser"
import { useCallback } from "react"
import { z } from "zod"
import { LocationSearchResult } from "../../searchResults/LocationSearchResult"
import { definePlugin } from "../types"

export const locationSearchResultDataSchema = z.object({
    title: z.string(),
    subtitle: z.string().optional(),
    center: z.tuple([z.number(), z.number()]),
    placeType: z.string(),
})

export type LocationSearchResultData = z.infer<typeof locationSearchResultDataSchema>

const coordinatePattern = /^-?\d{1,3}(?:\.\d+)?(?:, ?|\s)-?\d{1,3}(?:\.\d+)?$/

const parseCoordinate = (query: string): LocationSearchResultData | undefined => {
    if (!coordinatePattern.test(query)) return undefined

    let split = query.split(",")
    if (split.length === 1) split = query.split(" ")
    if (split.length !== 2) return undefined

    const [latString, lngString] = [split[0].trimEnd(), split[1].trim()]

    return {
        title: `${latString.slice(0, 8)}º, ${lngString.slice(0, 8)}º`,
        placeType: "coordinate",
        center: [Number(lngString), Number(latString)],
    }
}

const searchGeocode = async (
    query: string,
    signal?: AbortSignal
): Promise<LocationSearchResultData[]> => {
    const response = await fetch(
        `https://api.maptiler.com/geocoding/${encodeURIComponent(query)}.json?key=${config.get(
            "mapTilerApiKey"
        )}&limit=5&proximity=ip`,
        { signal }
    )
    const data = await response.json()
    return ((data.features ?? []) as Record<string, unknown>[]).map((feature) => {
        const parts = (feature.place_name as string).split(",").map((part) => part.trim())
        return {
            title: parts[0] || "",
            subtitle: parts.slice(1).join(", "),
            center: feature.center as [number, number],
            placeType: (feature.place_type as string[])[0],
        }
    })
}

export const locationsPlugin = definePlugin({
    name: "locations",
    displayName: "Locations",
    useSearch: () => {
        return useCallback(async (query, ctx): Promise<LocationSearchResultData[]> => {
            const coordinateResult = parseCoordinate(query)
            if (coordinateResult) return [coordinateResult]
            if (query.length <= 2) return []
            return searchGeocode(query, ctx.signal)
        }, [])
    },
    dataSchema: locationSearchResultDataSchema,
    Component: LocationSearchResult,
    getKey: (data) => `${data.placeType}:${data.center.join(",")}`,
    getSearchableText: (data) => `${data.title} ${data.subtitle ?? ""}`,
})
